import { useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash/debounce'

/**
 * How long a typed field waits, after the last keystroke, before it writes to Formik.
 *
 * Matches the delay `StringOption` and `SocialLinkRow` already use for the inspector's typed
 * fields, so every text input in the editor settles on the same rhythm.
 */
export const COMMIT_DEBOUNCE_MS = 400

export interface DebouncedCommit<T> {
  /** Schedule a commit. Replaces any commit already scheduled. */
  schedule: (value: T) => void
  /** Drop a scheduled commit without running it — for when the caller is about to commit itself. */
  cancel: () => void
}

/**
 * Commits a typed value to Formik on a debounce instead of waiting for blur/click-away (#23714).
 *
 * The canvas's text fields keep their value in local state and hand it to Formik only when the
 * field blurs (`InlineEditableText`, the Custom block's code editors) or when the block loses
 * focus (`InlineEditableHtml`). Everything else in a block — media picks, toggles, sliders —
 * commits on change, so only text left the form reading clean while there were real edits on
 * screen. `isDirty` is derived from that form (PresentationEdit's `onSubmit`), so Save disabled
 * itself exactly when the user reached for it, and MUI's `pointer-events: none` on a disabled
 * button meant their click fell through to the canvas click-away instead: the first click
 * committed the edit, the second one saved. Committing while they type puts text on the same
 * footing as every other edit, and the button is live by the time they get to it.
 *
 * With autosave off — the only mode the v2 builder ships — the `submitForm()` inside a block's
 * `handleEdit` does no network work: `onSubmit` recomputes `isDirty` against the saved document
 * and stops, because the save branch needs `manualSave`. Recomputing rather than setting a flag is
 * the point: type something and retype the original, and `isDirty` goes back to false on its own,
 * which `saveStatus` and `RoutePrompt` both depend on.
 *
 * A window still remains between the last keystroke and this timer, which is why
 * `PresentationEditTopBar`'s `handleSave` also blurs the active element before submitting.
 *
 * @param commit the caller's commit function — read from a ref, so it is always the latest one
 */
export function useDebouncedCommit<T>(commit: (value: T) => void): DebouncedCommit<T> {
  // The debounced function is created once for the component's lifetime, so it must never close
  // over `commit` directly: these callers rebuild it every render (a block's `handleEdit` captures
  // the block's index), and a debounce holding the render-one closure would commit to whatever
  // that stale one pointed at. Same reasoning as SocialLinkRow's `onChangeRef`.
  const commitRef = useRef(commit)
  commitRef.current = commit

  const debounced = useMemo(
    () =>
      debounce((value: T) => {
        commitRef.current(value)
      }, COMMIT_DEBOUNCE_MS),
    []
  )

  // A pending commit that fires after unmount writes to a Formik tree this field is no longer part
  // of — at best wasted, at worst resurrecting a value from a block that has just been deleted.
  useEffect(() => () => debounced.cancel(), [debounced])

  return useMemo(
    () => ({
      schedule: debounced,
      cancel: () => debounced.cancel()
    }),
    [debounced]
  )
}
